import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, bigArt } from "@/lib/api";
import TrackRow from "@/components/TrackRow";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Pause, Clock3, CalendarClock, Trash2 } from "lucide-react";
import { useMeta } from "@/lib/useMeta";
import ScheduleReleaseDialog from "@/components/ScheduleReleaseDialog";
import { toast } from "sonner";

const Album = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduled, setScheduled] = useState([]);
  const [profile, setProfile] = useState(null);
  const { playTracks, current, playing, togglePlay } = usePlayer();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get(`/album/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Load artist-scheduled upcoming tracks tied to this album
  useEffect(() => {
    if (!id) return;
    api.get(`/upcoming/album/${id}`)
      .then((r) => setScheduled(r.data?.results || []))
      .catch(() => setScheduled([]));
  }, [id]);

  // Verified artist owning this album?
  useEffect(() => {
    if (!user) { setProfile(null); return; }
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [user]);

  useMeta({
    title: data?.album ? `${data.album.collectionName} · ${data.album.artistName} · 1o1sound` : "Album · 1o1sound",
    description: data?.album
      ? `Listen to ${data.album.collectionName} by ${data.album.artistName} on 1o1sound.`
      : undefined,
    image: data?.album ? bigArt(data.album.artworkUrl100) : undefined,
  });

  if (loading) {
    return <div className="text-white/40">Loading album...</div>;
  }
  if (!data?.album) {
    return <div className="text-white/60">Album not found.</div>;
  }

  const { album, tracks } = data;
  const playable = tracks.filter((t) => t.previewUrl);
  const isAlbumPlaying = playing && playable.some((t) => t.trackId === current?.trackId);
  const canSchedule =
    profile?.is_artist &&
    profile?.artist_itunes_id &&
    album?.artistId &&
    String(profile.artist_itunes_id) === String(album.artistId);

  const reloadScheduled = () => {
    api.get(`/upcoming/album/${id}`)
      .then((r) => setScheduled(r.data?.results || []))
      .catch(() => {});
  };

  const deleteScheduled = async (releaseId, title) => {
    if (!window.confirm(`Remove "${title}"?`)) return;
    try {
      await api.delete(`/upcoming/artist-submit/${releaseId}`);
      toast.success("Removed");
      reloadScheduled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div data-testid="album-page" className="pb-16">
      {/* Hero */}
      <div className="relative -mx-4 sm:-mx-8 -mt-8 px-4 sm:px-8 py-10 mb-8 overflow-hidden">
        <div
          className="absolute inset-0 -z-10 blur-3xl opacity-60"
          style={{
            background: `radial-gradient(800px circle at 30% 30%, rgba(255,51,102,0.3), transparent 60%), radial-gradient(600px circle at 70% 60%, rgba(157,76,221,0.25), transparent 60%), #050505`,
          }}
        />
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end">
          <img
            src={bigArt(album.artworkUrl100)}
            alt={album.collectionName}
            className="w-48 h-48 sm:w-56 sm:h-56 rounded-lg shadow-2xl object-cover"
          />
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2">Album</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-none">{album.collectionName}</h1>
            <div className="mt-4 text-sm text-white/60 flex flex-wrap items-center gap-2">
              <a href={`/artist/${album.artistId}`} className="text-white font-medium hover:underline">{album.artistName}</a>
              <span>•</span>
              <span>{album.primaryGenreName}</span>
              {album.releaseDate && <><span>•</span><span>{new Date(album.releaseDate).getFullYear()}</span></>}
              <span>•</span>
              <span>{tracks.length} songs</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6 flex-wrap">
          <button
            data-testid="album-play-btn"
            onClick={() => {
              if (playable.length === 0) return;
              if (isAlbumPlaying) {
                togglePlay();
              } else {
                playTracks(playable, 0);
              }
            }}
            disabled={playable.length === 0}
            className="w-14 h-14 rounded-full bg-[#FF2E00] text-white flex items-center justify-center hover:scale-105 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition-all disabled:opacity-30"
          >
            {isAlbumPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />}
          </button>
          {canSchedule && (
            <ScheduleReleaseDialog
              collectionId={String(album.collectionId)}
              collectionName={album.collectionName}
              artistName={album.artistName}
              artworkUrl={album.artworkUrl100}
              onCreated={reloadScheduled}
              testId="album-schedule-release"
            />
          )}
        </div>
      </div>

      {/* Scheduled tracks coming on this album */}
      {scheduled.length > 0 && (
        <div className="mb-8" data-testid="album-scheduled-section">
          <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-[0.2em] text-[#E2FF31] font-bold">
            <CalendarClock size={13} /> Coming on this album
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[#E2FF31]/8 to-transparent border border-[#E2FF31]/20 overflow-hidden">
            {scheduled.map((r) => {
              const isMine = profile?.user_id && r.submitted_by === profile.user_id;
              return (
                <div
                  key={r.release_id}
                  data-testid={`album-scheduled-${r.release_id}`}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.03] border-b border-white/5 last:border-b-0 transition"
                >
                  <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center shrink-0">
                    <CalendarClock size={16} className="text-[#E2FF31]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{r.release_title}</p>
                    <p className="text-xs text-white/55">
                      {r.expected_release_date
                        ? new Date(r.expected_release_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                        : "Date TBA"}
                      {" · "}
                      <span className="capitalize">{r.release_type || "single"}</span>
                    </p>
                  </div>
                  {isMine && (
                    <button
                      data-testid={`album-scheduled-delete-${r.release_id}`}
                      onClick={() => deleteScheduled(r.release_id, r.release_title)}
                      className="p-2 rounded-full text-white/40 hover:text-[#FF2E00] hover:bg-white/5 transition"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Track list header */}
      <div className="grid grid-cols-[24px_1fr_auto] sm:grid-cols-[24px_1fr_1fr_auto_auto] gap-4 px-4 pb-2 border-b border-white/5 text-xs uppercase tracking-wider text-white/40">
        <span>#</span>
        <span>Title</span>
        <span className="hidden sm:block">Album</span>
        <span></span>
        <span className="hidden sm:flex justify-end"><Clock3 size={14} /></span>
      </div>

      <div className="mt-2">
        {tracks.map((t, i) => (
          <TrackRow key={t.trackId} track={{ ...t, artworkUrl100: t.artworkUrl100 || album.artworkUrl100 }} index={i} queue={playable} />
        ))}
      </div>
    </div>
  );
};

export default Album;
